import { ArrowBack } from "@mui/icons-material";
import { Box, Divider, IconButton, List, ListItem, ListItemText, Stack, Typography } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { DateTimePickerElement } from "react-hook-form-mui/date-pickers";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { MapPanelHeader, MapPanelScrollableArea } from "~/modules/map/components/MapPanel";
import LinkableItemList from "~/modules/mapEditor/components/LinkableItemList";
import { MapEditorFeatureProperties } from "~/modules/mapEditor/components/MapEditorFeatureProperties";
import { ObservationCard } from "~/modules/mapEditor/components/ObservationCard";
import { ObservationCardFragment } from "~/modules/mapEditor/components/ObservationCard/fragments.generated";
import { ObservationMediaDisplay } from "~/modules/mapEditor/components/ObservationMediaDisplay";

import {
    mergeObservationMediaPointSpecies,
    ObservationMediaPointSpeciesChip,
} from "./components/ObservationMediaPointSpeciesChip";
import { MapEditorObservationMediaPointPanelFragment } from "./fragments.generated";
import { useSuspenseObservationMediaPointQuery } from "./useSuspenseObservationMediaPointQuery";
import {
    ObservationMediaPointUpdateFormData,
    useUpdateObservationMediaPointMutation,
} from "./useUpdateObservationMediaPointMutation";

export interface MapEditorObservationMediaPointPanelEditableProps {
    backHref: string;
    createHrefToObservation: (element: ObservationCardFragment) => string;
    mapEditorFeatureId: string;
    title: string;
}

export function MapEditorObservationMediaPointPanelEditable({
    backHref,
    createHrefToObservation,
    mapEditorFeatureId,
    title,
}: MapEditorObservationMediaPointPanelEditableProps) {
    const { data } = useSuspenseObservationMediaPointQuery(mapEditorFeatureId);
    const [updateAction] = useUpdateObservationMediaPointMutation();

    const mediaPoint = data.mapEditorFeatureGetById.data as MapEditorObservationMediaPointPanelFragment;

    return (
        <MapEditorObservationMediaPointPanelEditableImpl
            backHref={backHref}
            createHrefToObservation={createHrefToObservation}
            mediaPoint={mediaPoint}
            onUpdate={values => updateAction(mediaPoint.id, values)}
            title={title}
        />
    );
}

interface MapEditorObservationMediaPointPanelEditableImplProps {
    backHref: string;
    createHrefToObservation: (element: ObservationCardFragment) => string;
    mediaPoint: MapEditorObservationMediaPointPanelFragment;
    onUpdate: (values: ObservationMediaPointUpdateFormData) => Promise<unknown>;
    title: string;
}

function MapEditorObservationMediaPointPanelEditableImpl({
    backHref,
    createHrefToObservation,
    mediaPoint,
    onUpdate,
    title,
}: MapEditorObservationMediaPointPanelEditableImplProps) {
    const { t } = useTranslation("mapEditor");

    const { control, handleSubmit } = useForm<ObservationMediaPointUpdateFormData>({
        values: createInitialFormData(mediaPoint),
        disabled: false,
    });

    const handleOnSubmit: SubmitHandler<ObservationMediaPointUpdateFormData> = async values => {
        await onUpdate(values);
    };

    return (
        <>
            <MapPanelHeader>
                <IconButton component={Link} color="inherit" to={backHref} relative="path">
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6" flex={1}>
                    {title}
                </Typography>
            </MapPanelHeader>
            <Box
                component="form"
                flex={1}
                minHeight={0}
                display="flex"
                flexDirection="column"
                onSubmit={handleSubmit(handleOnSubmit)}
            >
                <MapPanelScrollableArea>
                    <Stack gap={2} padding={2}>
                        <DateTimePickerElement
                            control={control}
                            label={t("featureDetailMediaPoint.createdAt")}
                            name="createdAt"
                            disabled
                        />

                        <ObservationMediaDisplay media={[mediaPoint.media]} />

                        {mediaPoint.observationMediaPointSpecies.length > 0 && (
                            <>
                                <Divider>{t("featureDetailMediaPoint.detections")}</Divider>
                                <List disablePadding>
                                    {mergeObservationMediaPointSpecies(mediaPoint.observationMediaPointSpecies).map(x => (
                                        <ListItem key={x.species.id} disablePadding>
                                            <ListItemText
                                                primary={x.species.name}
                                                secondary={x.processors.map(processor => {
                                                    return (
                                                        <ObservationMediaPointSpeciesChip
                                                            key={processor.label}
                                                            confidence={processor.confidence}
                                                            species={processor.label}
                                                            detections={processor.detections}
                                                        />
                                                    );
                                                })}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        )}

                        {mediaPoint.observations.length > 0 && (
                            <>
                                <Divider>{t("mapEditorFeatureDetails.labelObservations")}</Divider>
                                <LinkableItemList
                                    clickable
                                    createTo={createHrefToObservation}
                                    items={mediaPoint.observations}
                                    renderCard={element => <ObservationCard key={element.id} observation={element} />}
                                />
                            </>
                        )}

                        <Divider>{t("mapEditorFeatureDetails.labelProperties")}</Divider>
                        <MapEditorFeatureProperties properties={mediaPoint.feature.properties} />
                    </Stack>
                </MapPanelScrollableArea>
                <Stack p={2} gap={1} sx={theme => ({ borderTop: `1px solid ${theme.palette.divider}` })}>
                    <GaiaHookFormSubmitButton control={control}>{t("featureDetailMediaPoint.btnSave")}</GaiaHookFormSubmitButton>
                </Stack>
            </Box>
        </>
    );
}

function createInitialFormData(mediaPoint: MapEditorObservationMediaPointPanelFragment): ObservationMediaPointUpdateFormData {
    return {
        createdAt: parseStringToDate(mediaPoint.createdAt),
        speciesIds: mediaPoint.observationMediaPointSpecies.map(species => species.species.id),
    };
}
